import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { msg } from '../../localization/i18n';
import { cn } from '../../lib/utils';
import type { TovIconName } from '../../contracts/iconography';
import { TovIcon } from '../TovIcon/TovIcon';
import { TovButton, type TovButtonProps } from '../TovButton/TovButton';
import { Dialog } from '../TovModalDialog/TovModalDialog';

export interface InstallAppButtonProps
  extends
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    Pick<TovButtonProps, 'variant'> {
  iconName?: TovIconName;
  installed?: boolean;
  installedLabel: string;
  installing?: boolean;
  installingLabel: string;
  label: string;
}

export function InstallAppButton({
  className,
  disabled,
  iconName = 'device-mobile-camera',
  installed = false,
  installedLabel,
  installing = false,
  installingLabel,
  label,
  type = 'button',
  variant = 'secondary',
  ...props
}: InstallAppButtonProps) {
  const resolvedLabel = installed
    ? installedLabel
    : installing
      ? installingLabel
      : label;

  return (
    <TovButton
      className={cn('install-app-button', className)}
      disabled={disabled || installing || installed}
      type={type}
      variant={variant}
      {...props}
    >
      <TovIcon name={iconName} />
      <span>{resolvedLabel}</span>
    </TovButton>
  );
}

export interface InstallAppBannerProps {
  action: ReactNode;
  className?: string;
  description: ReactNode;
  eyebrow?: ReactNode;
  title: ReactNode;
}

export function InstallAppBanner({
  action,
  className,
  description,
  eyebrow,
  title,
}: InstallAppBannerProps) {
  return (
    <section className={cn('install-app-banner', className)}>
      <div className="install-app-banner__copy">
        {eyebrow ? (
          <p className="install-app-banner__eyebrow">{eyebrow}</p>
        ) : null}
        <h2 className="install-app-banner__title">{title}</h2>
        <p className="install-app-banner__description">{description}</p>
      </div>
      <div className="install-app-banner__action">{action}</div>
    </section>
  );
}

export interface InstallAppModalProps {
  children: ReactNode;
  description?: ReactNode;
  footerChildren?: ReactNode;
  open: boolean;
  onOpenChange(open: boolean): void;
  title: ReactNode;
}

export function InstallAppModal({
  children,
  description,
  footerChildren,
  onOpenChange,
  open,
  title,
}: InstallAppModalProps) {
  return (
    <Dialog
      bodyClassName="install-app-modal__body"
      contentClassName="install-app-modal"
      description={description}
      footerChildren={footerChildren}
      open={open}
      onOpenChange={onOpenChange}
      title={title}
    >
      {children}
    </Dialog>
  );
}

export interface IosInstallInstructionsProps {
  addStep: ReactNode;
  description: ReactNode;
  hint?: ReactNode;
  shareStep: ReactNode;
}

export function IosInstallInstructions({
  addStep,
  description,
  hint,
  shareStep,
}: IosInstallInstructionsProps) {
  return (
    <div className="ios-install-instructions">
      <p className="ios-install-instructions__description">{description}</p>
      <ol className="ios-install-instructions__steps">
        <li>
          <span
            className="ios-install-instructions__step-icon"
            aria-hidden="true"
          >
            <TovIcon name="upload" />
          </span>
          <div>
            <span>{shareStep}</span>
          </div>
        </li>
        <li>
          <span
            className="ios-install-instructions__step-icon"
            aria-hidden="true"
          >
            <TovIcon name="plus" />
          </span>
          <div>
            <span>{addStep}</span>
          </div>
        </li>
      </ol>
      {hint ? <p className="ios-install-instructions__hint">{hint}</p> : null}
      <p className="ios-install-instructions__footnote">
        {msg(
          'components.install_app.components_install_app_ios_safari_requires_manual_install_steps',
        )}
      </p>
    </div>
  );
}
